import Table, { TableDetails } from "@/components/Table";
import { CellContext, ColumnDef, SortingState } from "@tanstack/react-table";
import Icon from "@/components/Icon";
import Avatar from "@/components/Avatar";
import IconButton from "@/components/IconButton";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { BackofficePerson } from "@/types";
import { useEffect, useState } from "react";
import EmptyState from "@/components/EmptyState";
import { JobPost, ServiceLevel, isExecuSearchServiceLevel, PostStage, HiringStatus } from "../types";
import { toDateFormat } from "@/lib/dateHelpers";
import ApplicantDetailsDialog from "./ApplicantDetailsDialog";
import CopyButton from "@/components/CopyButton";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import { useRequireCapability, useCanManageJobPostsBool } from "@/hooks/useCapabilities";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { DELETE_JOB_APPLICATION, JOB_POST_WITH_APPLICATIONS_QUERY, updateCache } from "../queries";
import { showSnackbar } from "@/lib/snackbarUtils";

import CreateApplicationReviewDialog from "./CreateApplicationReviewDialog";
import { useTopActionBar, SideBar } from "@/layouts/AppLayout/TopActionBarContext";
import CreateJobApplicationDialog from "./CreateJobApplicationDialog";
import { ensureProtocol, isDev } from "@/lib/urlUtils";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import MetricGrid from "@/components/MetricGrid";
import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import { chain } from "lodash";
import TableDetailsPanel from "@/components/TableDetailsPanel";
import { RowType } from "@/components/Table/Table";
import { Row } from "@tanstack/react-table";
import UpdateJobApplicationFilesModal from "@/components/UpdateJobApplicationFilesModal";
import { useJobPostContext } from "@/pages/_layouts/jobs/useJobPostContext";

export interface Applicant {
  applicant: BackofficePerson;
  email: string;
  phone: string;
  resume?: string;
  coverLetter?: string;
  website?: string;
  appliedOn: string;
  id: string;
  customApplicationData: CustomApplicationData;
}
export interface CustomApplicationData {
  currentOrMostRecentEmployer: string;
  currentOrMostRecentPosition: string;
  referralSource: {
    id: string;
    content: string;
  }[];
  id: string;
  answers: CustomApplicationAnswer[];
}

export interface CustomApplicationAnswer {
  selectedOption: {
    content: string;
  };
  customText: string;
  answerType: string;
  content: string;
  id: string;
  position: string;
  question: {
    id: string;
    content: string;
    position: number;
  };
}

interface ApplicationAnswerRow extends RowType {
  questionId: string;
  question: string;
  position: number;
  answer: string;
}

const formatAnswers = (answers: CustomApplicationAnswer[]) =>
  chain(answers)
    .groupBy((a) => a?.question?.id)
    .map((group) => ({
      questionId: group[0]?.question?.id,
      question: group[0]?.question?.content,
      position: group[0]?.question?.position,
      answer: group
        .map((a) => (a?.answerType === "custom_text" ? a?.customText : a?.selectedOption?.content))
        .join(", ")
    }))
    .sortBy("position")
    .value();

const buildAnswers = (row: Applicant) => {
  const { currentOrMostRecentPosition, currentOrMostRecentEmployer, referralSource, answers } =
    row.customApplicationData || {};
  const formattedAnswers = formatAnswers(answers);

  return [
    {
      question: "Current or most recent position",
      answer: currentOrMostRecentPosition
    },
    {
      question: "Current or most recent employer",
      answer: currentOrMostRecentEmployer
    },
    {
      question: "How did you learn about this job opportunity",
      answer: referralSource?.join(", ")
    },
    ...formattedAnswers
  ];
};

const columns = (
  setMenu: (options: any) => void,
  onViewDetails: (applicant: Applicant) => void,
  onClickDelete: (applicant: Applicant) => void,
  onClickAddInteraction: (applicant: Applicant) => void,
  onClickAddReview: (applicant: Applicant) => void,
  onClickUpdateFiles: (applicant: Applicant) => void,
  jobPost: JobPost,
  canManageJobPosts: boolean
): ColumnDef<Applicant>[] => {
  const isAdvancedOrExecutive = isExecuSearchServiceLevel(jobPost.serviceLevel);

  const allColumns: ColumnDef<Applicant>[] = [
    {
      id: "name",
      header: "Applicant Name",
      size: 400,
      accessorFn: (row) => `${row.applicant?.firstName || ""} ${row.applicant?.lastName || ""}`.trim() || "",
      cell: ({ row }) => (
        <div className="flex items-center py-2">
          <Avatar className="min-w-[3rem]" picture={row.original?.applicant?.profilePhoto} />
          <div className="ml-2">
            <span className="text-body-medium">
              {row.original?.applicant?.firstName} {row.original?.applicant?.lastName}
            </span>
            <br />
            <span className="text-body-small">{row.original?.applicant?.latestProgram?.programType?.name}</span>
          </div>
        </div>
      )
    },
    {
      id: "section",
      header: "Section",
      accessorFn: (row) => row.applicant?.primarySection?.name || "",
      cell: ({ row }) => (
        <div className="flex items-center py-2">
          {row.original?.applicant?.primarySection?.name || <span className="opacity-20">--</span>}
        </div>
      )
    },
    {
      id: "email",
      header: "Email",
      accessorFn: (row) => row.email || "",
      cell: ({ row }) => (
        <div className="flex items-center justify-between gap-1">
          <a href={`mailto:${row.original.email}`} onClick={(e) => e.stopPropagation()}>
            {row.original.email}
          </a>
          <CopyButton textToCopy={row.original.email} copyFieldLabel="Email" />
        </div>
      )
    },
    {
      id: "phone",
      header: "Phone",
      accessorFn: (row) => row.phone || "",
      enableSorting: false,
      cell: ({ row }) => {
        const formatted = formatPhoneNumber(row.original.phone);
        return (
          <div className="flex items-center justify-between gap-1">
            <a onClick={(e) => e.stopPropagation()} href={`tel:${row.original.phone}`}>
              {formatted}
            </a>
            {formatted && <CopyButton textToCopy={formatted} copyFieldLabel="Phone" />}
          </div>
        );
      }
    },
    {
      id: "resume",
      header: "Resume",
      accessorKey: "resume",
      enableSorting: false,
      cell: ({ row }) =>
        row.original.resume ? (
          <a
            href={row.original.resume}
            className="text-sys-brand-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noreferrer"
          >
            Resume
          </a>
        ) : (
          <span className="opacity-20">--</span>
        )
    },
    {
      id: "coverLetter",
      header: "Cover Letter",
      accessorKey: "coverLetter",
      cell: ({ row }) =>
        row.original.coverLetter ? (
          <a
            href={row.original.coverLetter}
            className="text-label-large text-sys-brand-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noreferrer"
          >
            Cover Letter
          </a>
        ) : (
          <span className="opacity-20">--</span>
        )
    },
    {
      id: "website",
      header: "Website",
      accessorKey: "website",
      cell: ({ row }) =>
        row.original.website ? (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Icon name="open_in_new" className="text-sys-brand-primary" size={18} />
            <a
              href={ensureProtocol(row.original.website, false)}
              target="_blank"
              rel="noreferrer"
              className="text-sys-brand-primary text-label-large hover:underline"
            >
              Website
            </a>
          </div>
        ) : (
          <span className="opacity-20">--</span>
        )
    },
    ...(isAdvancedOrExecutive
      ? [
          {
            id: "answers",
            header: "Answers",
            accessorKey: "answers",
            enableSorting: false,
            cell: ({ row }: { row: Row<Applicant> }) => {
              return row.original.customApplicationData ? (
                <div
                  className="flex items-center py-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    row.getToggleExpandedHandler()();
                  }}
                >
                  <span>{buildAnswers(row.original).length}</span>
                  <Icon name={row.getIsExpanded() ? "expand_less" : "expand_more"} className="max-md:hidden" />
                </div>
              ) : (
                <span className="opacity-20">--</span>
              );
            }
          }
        ]
      : []),
    {
      id: "appliedOn",
      header: "Applied On",
      accessorFn: (row) => row.appliedOn || "",
      cell: ({ row }) => (row.original.appliedOn ? toDateFormat(row.original.appliedOn, { tz: "utc" }) : "")
    },
    {
      id: "actions",
      header: "",
      cell: (cellProps: CellContext<Applicant, unknown>) => {
        const {
          row: { id: rowId, original }
        } = cellProps;

        const actions: Action[] = [
          {
            text: "Add Interaction",
            section: "Manage",
            onClick: () => {
              onClickAddInteraction(original);
            }
          },
          ...(jobPost.serviceLevel !== "Self-Serve" && jobPost.serviceLevel !== "Assisted"
            ? [
                {
                  text: "Add Review",
                  section: "Manage",
                  onClick: () => {
                    onClickAddReview(original);
                  }
                } satisfies Action
              ]
            : []),
          ...(canManageJobPosts
            ? [
                {
                  text: "Update Files",
                  section: "Manage",
                  onClick: () => {
                    onClickUpdateFiles(original);
                  }
                } satisfies Action
              ]
            : []),
          {
            text: "Delete Application",
            iconName: "delete",
            destructive: true,
            onClick() {
              onClickDelete(original);
            }
          }
        ];

        if (!actions.length) return null;

        function onClick(e: React.MouseEvent) {
          e.stopPropagation();
          setMenu({ rowId, actions });
        }

        return (
          <div className="flex items-center gap-2 justify-end">
            <IconButton
              onClick={() => {
                onViewDetails(original);
              }}
              data-testid="expand-details"
              title="View Details"
              name="open_in_full"
              iconClassName="text-sys-surface-variant"
            />
            <IconButton
              id={actionId(rowId)}
              onClick={onClick}
              data-testid="actions"
              title="actions"
              name="more_vert"
              iconClassName="font-extrabold"
            />
          </div>
        );
      }
    }
  ];

  if (isAdvancedOrExecutive) {
    return allColumns.filter((column) => column.id !== "coverLetter" && column.id !== "website");
  }

  return allColumns;
};

interface ViewJobApplicantsProps {
  jobPost: JobPost;
}

const getSalesforceJobPostUrl = (jobPostId: string) => {
  const baseUrl = isDev()
    ? "https://pgaservices--staging.sandbox.lightning.force.com/lightning/r/Job_Post__c"
    : "https://pgaservices.lightning.force.com/lightning/r/Job_Post__c";
  return `${baseUrl}/${jobPostId}/view`;
};

export default function ViewJobApplicants({ jobPost }: ViewJobApplicantsProps) {
  const { Menu, setMenu } = useTableRowMenu();
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [metrics, setMetrics] = useState<MetricCardType<Path>[]>([]);
  const canManageJobPosts = useCanManageJobPostsBool();
  const { isJobFilledOrIncomplete } = useJobPostContext();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [applicantToDelete, setApplicantToDelete] = useState<Applicant | null>(null);
  const [applicants, setApplicants] = useState<any[]>([]);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [reviewApplicant, setReviewApplicant] = useState<Applicant | null>(null);
  const { showSidebar } = useTopActionBar();
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const { data, loading, refetch, error } = useQuery<{ jobPost: JobPost }>(JOB_POST_WITH_APPLICATIONS_QUERY, {
    variables: { jobPostId: jobPost?.id },
    skip: !jobPost?.id,
    fetchPolicy: "cache-and-network"
  });

  const { cache } = useApolloClient();

  useEffect(() => {
    if (data) {
      setApplicants(data.jobPost.applications || []);
      updateCache(cache, data.jobPost.id, "applications", data.jobPost.applications);
    } else {
      setApplicants([]);
    }
  }, [cache, data]);

  useRequireCapability({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.manageJobPosts
  });

  const [deleteJobApplication, { loading: isDeleting }] = useMutation(DELETE_JOB_APPLICATION, {
    onCompleted: () => {
      setIsDeleteModalOpen(false);
      setApplicantToDelete(null);
      refetch();
      showSnackbar(
        `Successfully deleted ${applicantToDelete?.applicant.firstName} ${applicantToDelete?.applicant.lastName}'s application`,
        { close: true }
      );
    },
    onError: () => {
      showSnackbar("Failed to delete application", { close: true });
    }
  });

  const handleClickDelete = (applicant: Applicant) => {
    if (!applicant.id) {
      showSnackbar("Applicant ID is missing", { close: true });
      return;
    }
    setApplicantToDelete({
      ...applicant,
      id: applicant.id
    });
    setIsDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (!applicantToDelete?.id) {
      showSnackbar("Applicant ID is missing", { close: true });
      return;
    }

    deleteJobApplication({
      variables: {
        applicationId: applicantToDelete.id
      }
    });
  };

  const handleViewDetails = (applicant: Applicant) => {
    setSelectedApplicant(applicant);
    setIsSidebarOpen(true);
  };

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedApplicant(null);
  };

  useEffect(() => {
    const displayedCount = applicants.length;

    setMetrics([
      {
        label: "Total Applicants",
        value: `${displayedCount}`,
        iconName: "business_center"
      },
      {
        label: "Days Remaining to Apply",
        value: `${Math.max(
          0,
          Math.round((new Date(jobPost.submissionDeadline).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
        )}`,
        iconName: "today"
      },
      {
        label: "Days Published",
        value: `${Math.round((new Date().getTime() - new Date(jobPost.createdAt).getTime()) / (1000 * 60 * 60 * 24))}`,
        iconName: "today"
      }
    ]);
  }, [applicants, jobPost.submissionDeadline, jobPost.createdAt]);

  const handleAddInteraction = (applicant: Applicant) => {
    showSidebar(SideBar.CREATE_GLOBAL_INTERACTION, {
      pgaId: applicant.applicant.pgaId,
      defaultJobPostId: jobPost.id
    })();
  };

  const handleAddReview = (applicant: Applicant) => {
    setReviewApplicant(applicant);
    setIsReviewDialogOpen(true);
  };

  const handleUpdateFiles = (applicant: Applicant) => {
    if (!applicant.id) {
      showSnackbar("Application ID is missing", { close: true });
      return;
    }
    setSelectedApplicationId(applicant.id);
    setUpdateModalOpen(true);
  };

  const tableDetailsContent = () => (
    <span className="flex items-center gap-1 text-sys-brand-primary text-label-large cursor-pointer">
      <CopyButton textToCopy={jobPost.jobApplicantsListUrl || ""} copyFieldLabel="Applicant List Link">
        Copy Applicant List Link
      </CopyButton>
    </span>
  );

  const addApplicantButton = () => (
    <Button variant="filledTonal" icon="add" onClick={() => setIsCreateDialogOpen(true)}>
      Add Applicant
    </Button>
  );

  const isFilledOrIncompleteJob = isJobFilledOrIncomplete(jobPost);

  const shouldDisplayTableDetails =
    !isFilledOrIncompleteJob &&
    (jobPost.serviceLevel === "Advanced" || jobPost.serviceLevel === "Executive") &&
    jobPost.jobApplicantsListUrl;

  const shouldShowAddApplicant =
    (jobPost.stage as PostStage) !== PostStage.DRAFT && (jobPost.status as HiringStatus) !== HiringStatus.FILLED;

  return (
    <div className="flex flex-col gap-3 mt-4">
      {!!metrics.length && <MetricGrid metrics={metrics} layout="expanded" />}

      <TableDetails
        heading="Applicants"
        body="View and manage all applications for this job"
        ButtonComps={[
          ...(shouldDisplayTableDetails ? [tableDetailsContent] : []),
          ...(shouldShowAddApplicant ? [addApplicantButton] : [])
        ]}
      />

      <Menu />
      <Table
        data={applicants}
        columns={columns(
          setMenu,
          handleViewDetails,
          handleClickDelete,
          handleAddInteraction,
          handleAddReview,
          handleUpdateFiles,
          jobPost,
          canManageJobPosts
        )}
        loading={loading}
        sortingState={sorting}
        onSortingChange={setSorting}
        renderFilteredEmptyState={() => {
          if (error) {
            return (
              <EmptyState
                title="Application Loading Issue"
                caption="Due to the high number of applications, this request is timing out. Please view applicants in Salesforce"
                iconName="error"
                action={{
                  children: "Link to Salesforce Job Post",
                  onClick: () => {
                    window.open(getSalesforceJobPostUrl(jobPost.id), "_blank");
                  },
                  icon: "open_in_new"
                }}
              />
            );
          }

          // Only show the facility-website message when Apply Via is Facility Website.
          // Advanced/Executive should never use Facility Website.
          if (
            jobPost.applyVia === "Facility Website" &&
            jobPost.serviceLevel !== ServiceLevel.ADVANCED &&
            jobPost.serviceLevel !== ServiceLevel.EXECUTIVE
          ) {
            return (
              <EmptyState
                title="Applications via Facility Website"
                caption="Applications for this job are submitted through the facility website and are not tracked here."
                iconName="person"
                action={{
                  children: "Facility Website",
                  onClick: () => {
                    window.open(jobPost.externalJobUrl, "_blank");
                  },
                  icon: "open_in_new"
                }}
              />
            );
          }

          return (
            <EmptyState title="No Applications" caption="There are no applications for this job." iconName="person" />
          );
        }}
        renderExpandedRow={({ row }) => {
          const answers = buildAnswers(row.original) as ApplicationAnswerRow[];
          return (
            <TableDetailsPanel
              data={answers}
              columns={[
                {
                  accessorKey: "question",
                  header: "Question"
                },
                {
                  accessorKey: "answer",
                  header: "Answer"
                }
              ]}
            />
          );
        }}
        onClickRow={(row) => {
          handleViewDetails(row.original);
        }}
        getRowClasses={() => "cursor-pointer hover:bg-sys-surface-container-highest"}
      />
      <ApplicantDetailsDialog
        applicant={selectedApplicant ? selectedApplicant.applicant : null}
        applicationEmail={selectedApplicant?.email}
        applicationPhone={selectedApplicant?.phone}
        open={isSidebarOpen}
        onClose={handleCloseSidebar}
      />
      <CreateApplicationReviewDialog
        open={isReviewDialogOpen}
        onClose={() => {
          setIsReviewDialogOpen(false);
          setReviewApplicant(null);
        }}
        jobApplicationId={reviewApplicant?.id || ""}
        applicantName={
          reviewApplicant ? `${reviewApplicant.applicant.firstName} ${reviewApplicant.applicant.lastName}` : ""
        }
        isRecommendation={false}
        onSuccess={refetch}
      />
      <Dialog
        open={isDeleteModalOpen}
        onClosed={() => setIsDeleteModalOpen(false)}
        headline="Delete Application"
        className="w-[490px] p-6 pr-2"
        actions={
          <div className="flex gap-2">
            <Button variant="text" onClick={() => setIsDeleteModalOpen(false)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="filled"
              theme="error"
              trailingIcon={false}
              icon="delete"
              onClick={handleDelete}
              disabled={isDeleting || !applicantToDelete?.id}
              data-testid="confirm-delete-application"
            >
              Delete
            </Button>
          </div>
        }
      >
        <p>
          Are you sure you want to delete this application? This will remove {applicantToDelete?.applicant.firstName}{" "}
          {applicantToDelete?.applicant.lastName}&apos;s application. This cannot be undone.
        </p>
      </Dialog>
      {updateModalOpen && selectedApplicationId && (
        <UpdateJobApplicationFilesModal
          isOpen={updateModalOpen}
          onClose={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
          }}
          applicationId={selectedApplicationId}
          onSuccess={() => {
            setUpdateModalOpen(false);
            setSelectedApplicationId(null);
            refetch();
          }}
        />
      )}
      <CreateJobApplicationDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        jobPost={jobPost}
        onSuccess={refetch}
      />
    </div>
  );
}
